import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminNavigation from './AdminNavigation';
import './Admin.css';
import { API_URL } from '../utils/config';

const Admin = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 10;
  
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    brand: 'Goldvines',
    description: '',
    category: '',
    stock: 100,
    colors: ''
  });
  const [selectedSizes, setSelectedSizes] = useState([]);
  
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    fetchProducts();
  }, [navigate]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products`);
      setProducts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setMessage({ text: 'Error loading products', type: 'error' });
      setLoading(false);
    }
  };

  // Pagination calculations
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = products.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(products.length / productsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 4) {
      setMessage({ text: 'Maximum 4 images allowed!', type: 'error' });
      return;
    }
    
    setImageFiles(files);
    setMainImageIndex(0);
    
    const previews = [];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        previews.push(reader.result);
        if (previews.length === files.length) {
          setImagePreviews(previews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const getImageUrl = (imagePath) => {
    if (imagePath && imagePath.startsWith('/uploads/')) {
      return `${API_URL}${imagePath}`;
    }
    return imagePath;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const productData = new FormData();
    productData.append('name', formData.name);
    productData.append('price', formData.price);
    productData.append('brand', formData.brand);
    productData.append('description', formData.description);
    productData.append('category', formData.category);
    productData.append('stock', formData.stock);
    
    const colorArray = formData.colors.split(',')
      .map(c => c.trim())
      .filter(Boolean);
    productData.append('colors', JSON.stringify(colorArray));
    productData.append('sizes', JSON.stringify(selectedSizes));
    
    imageFiles.forEach((file) => {
      productData.append('images', file);
    });
    productData.append('mainImageIndex', mainImageIndex);

    try {
      const adminToken = localStorage.getItem('adminToken');
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${adminToken}`
        }
      };

      if (editingProduct) {
        // Update existing product - use /api/admin/products
        await axios.put(
          `${API_URL}/api/admin/products/${editingProduct._id}`,
          productData,
          config
        );
        setMessage({ text: 'Product updated successfully!', type: 'success' });
      } else {
        // Create new product - use /api/admin/products
        await axios.post(
          `${API_URL}/api/admin/products`,
          productData,
          config
        );
        setMessage({ text: 'Product added successfully!', type: 'success' });
      }
      
      resetForm();
      fetchProducts();
      setShowForm(false);
      
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error saving product:', error);
      setMessage({ text: 'Error saving product. Please try again.', type: 'error' });
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      price: product.price,
      brand: product.brand || 'Goldvines',
      description: product.description || '',
      category: product.category || '',
      stock: product.stock || 100,
      colors: product.colors ? product.colors.join(', ') : ''
    });
    setSelectedSizes(product.sizes || []);
    
    const allImages = [product.image, ...(product.images || [])];
    setImagePreviews(allImages.map(img => getImageUrl(img)));
    
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const adminToken = localStorage.getItem('adminToken');
        // Use /api/admin/products for delete
        await axios.delete(`${API_URL}/api/admin/products/${id}`, {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });
        setMessage({ text: 'Product deleted successfully!', type: 'success' });
        fetchProducts();
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        console.error('Error deleting product:', error);
        setMessage({ text: 'Error deleting product', type: 'error' });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      brand: 'Goldvines',
      description: '',
      category: '',
      stock: 100,
      colors: ''
    });
    setSelectedSizes([]);
    setImageFiles([]);
    setImagePreviews([]);
    setMainImageIndex(0);
    setEditingProduct(null);
  };

  const cancelEdit = () => {
    resetForm();
    setShowForm(false);
  };

  return (
    <>
      <AdminNavigation />
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-content">
            <div>
              <h1>🛠️ Product Management</h1>
              <p>Add, edit, or delete products from your store</p>
            </div>
          </div>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="admin-actions">
          <button 
            className="btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}
          >
            {showForm ? '❌ Cancel' : '➕ Add New Product'}
          </button>
        </div>

        {showForm && (
          <div className="product-form-container">
            <h2>{editingProduct ? '✏️ Edit Product' : '➕ Add New Product'}</h2>
            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Product Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., RGB Controller"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Price (Taka) *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="e.g., 1400"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Brand</label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    placeholder="e.g., Goldvines"
                  />
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Category</option>
                    <option value="t-shirt">t-shirt</option>
                    <option value="polo-shirt">polo-shirt</option>
                    <option value="shirt">shirt</option>
                    <option value="money bag">money bag</option>
                    <option value="saree">saree</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Product Colors (Comma-separated, e.g., Red, Blue, Black)</label>
                <input
                  type="text"
                  name="colors"
                  value={formData.colors}
                  onChange={handleInputChange}
                  placeholder="e.g., Red, Blue, Black"
                />
              </div>

              {['t-shirt', 'polo-shirt', 'shirt'].includes(formData.category?.toLowerCase()) && (
                <div className="form-group">
                  <label style={{ marginBottom: '8px', display: 'block' }}>Available Sizes</label>
                  <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '5px', padding: '10px', background: '#f9f9f9', borderRadius: '4px' }}>
                    {['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'].map(size => (
                      <label key={size} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={selectedSizes.includes(size)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSizes([...selectedSizes, size]);
                            } else {
                              setSelectedSizes(selectedSizes.filter(s => s !== size));
                            }
                          }}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <span>{size}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Product Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Write a detailed description of your product here... Include features, specifications, and benefits."
                  rows="6"
                  required
                ></textarea>
                <small className="form-hint">
                  💡 Write a comprehensive description including features, specifications, and usage
                </small>
              </div>

              <div className="form-group">
                <label>Stock Quantity</label>
                <input
                  type="number"
                  name="stock"
                  value={formData.stock}
                  onChange={handleInputChange}
                  placeholder="e.g., 100"
                />
              </div>

              <div className="form-group">
                <label>Product Images (Maximum 4) *</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="file-input"
                />
                <small className="form-hint">
                  📸 First image will be the main product image. Upload up to 4 images total.
                </small>
                
                {imagePreviews.length > 0 && (
                  <div className="image-previews">
                    {imagePreviews.map((preview, index) => (
                      <div 
                        key={index} 
                        className={`image-preview ${index === mainImageIndex ? 'is-main' : ''}`}
                        onClick={() => setMainImageIndex(index)}
                        style={{ 
                          cursor: 'pointer', 
                          border: index === mainImageIndex ? '3px solid #667eea' : '3px dashed #e0e0e0',
                          position: 'relative'
                        }}
                      >
                        <img src={preview} alt={`Preview ${index + 1}`} />
                        <span className="image-label" style={{ 
                          background: index === mainImageIndex ? '#667eea' : 'rgba(0, 0, 0, 0.7)',
                          display: 'block',
                          width: '100%'
                        }}>
                          {index === mainImageIndex ? '⭐ Main Picture' : 'Click to Set Main'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">
                  {editingProduct ? '💾 Update Product' : '➕ Add Product'}
                </button>
                <button type="button" className="btn-cancel" onClick={cancelEdit}>
                  ❌ Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="products-list">
          <h2>📦 All Products ({products.length})</h2>
          
          {loading ? (
            <div className="loading">Loading products...</div>
          ) : products.length === 0 ? (
            <div className="no-products">
              <p>No products yet. Add your first product!</p>
            </div>
          ) : (
            <>
              <div className="products-grid">
                {currentProducts.map(product => (
                  <div key={product._id} className="product-card">
                    <div className="product-image">
                      <img 
                        src={getImageUrl(product.image)} 
                        alt={product.name}
                        onError={(e) => e.target.src = '/img/products/f1.jpg'}
                      />
                      {product.images && product.images.length > 0 && (
                        <span className="image-count">
                          📷 {product.images.length + 1} photos
                        </span>
                      )}
                    </div>
                    <div className="product-info">
                      <h3>{product.name}</h3>
                      <p className="product-brand">{product.brand}</p>
                      <p className="product-price">{product.price} Taka</p>
                      <p className="product-category">
                        📁 {product.category || 'Uncategorized'}
                      </p>
                      <p className="product-stock">
                        📦 Stock: {product.stock || 0} units
                      </p>
                      {product.description && (
                        <p className="product-description-preview">
                          {product.description.substring(0, 80)}...
                        </p>
                      )}
                    </div>
                    <div className="product-actions">
                      <button 
                        className="btn-edit"
                        onClick={() => handleEdit(product)}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDelete(product._id)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </button>

                  <div className="pagination-numbers">
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        className={`pagination-number ${currentPage === index + 1 ? 'active' : ''}`}
                        onClick={() => paginate(index + 1)}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>

                  <button
                    className="pagination-btn"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Admin;